import { useMemo } from "react";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from '@mui/icons-material/Info';
import { Filters as TransactionFilters } from "consts/transactions";
import { compareIds } from "util/support";
import { useTypedSelector } from "hooks";
import type { Option } from "types";
import type { AbstractFilterProps, Filter } from "ui/widgets/Filters";
import useFiltersModel from "ui/widgets/Filters/hooks/useFiltersModel";
import { selectTransactionCurrencies } from "features/general/selectors";
import { selectSeenFilterValues } from "features/transactions/selectors";
import type { SeenFilterValues } from "features/transactions/types";

const cascadingFilterIds: string[] = [
    TransactionFilters.currency,
    TransactionFilters.status,
    TransactionFilters.methodId,
    TransactionFilters.via,
    TransactionFilters.MOR,
    TransactionFilters.merchantId,
    TransactionFilters.PSP
];

const filterIdToSeenKey: Record<string, keyof SeenFilterValues> = {
    [TransactionFilters.currency]: 'currency',
    [TransactionFilters.status]: 'status',
    [TransactionFilters.methodId]: 'methodId',
    [TransactionFilters.via]: 'via',
    [TransactionFilters.MOR]: 'MOR',
    [TransactionFilters.merchantId]: 'merchantId',
    [TransactionFilters.PSP]: 'PSP'
};

const collectSelectedIds = (value: unknown): Set<string> => {
    const ids = new Set<string>();
    const push = (option: unknown) => {
        if (option && typeof option === 'object' && 'id' in option) {
            const id = (option as Option).id;
            if (id !== null && id !== undefined) {
                ids.add(String(id));
            }
        }
    };
    if (Array.isArray(value)) {
        value.forEach(push);
    } else {
        push(value);
    }
    return ids;
};

export const useFilterPropsOverride = (_: Filter<AbstractFilterProps>[]) => {
    const seenFilterValues = useTypedSelector(selectSeenFilterValues);
    const Currencies = useTypedSelector(selectTransactionCurrencies);
    const { getFilterOptions } = useFiltersModel({ Currencies });

    const narrowedOptionsByFilterId = useMemo(() => {
        const map = new Map<string, Option[]>();

        for (const filterId of cascadingFilterIds) {
            const seenKey = filterIdToSeenKey[filterId];
            const seen = seenFilterValues[seenKey];

            if (!seen || seen.length === 0) {
                continue;
            }

            const { options } = getFilterOptions(filterId) as { options?: Option[] };
            if (!options || options.length === 0) {
                continue;
            }

            const seenSet = new Set(seen);
            const narrowed = options.filter(option => seenSet.has(String(option.id)));

            if (narrowed.length === 0) {
                continue;
            }

            map.set(filterId, narrowed);
        }

        return map;
    }, [seenFilterValues, getFilterOptions]);

    const isDirectionFilter = (filter: Filter<AbstractFilterProps>) =>
        compareIds(filter.props.id, TransactionFilters.direction);

    return (filter: Filter<AbstractFilterProps>) => {
        const overrides: Record<string, unknown> = {};

        if (isDirectionFilter(filter)) {
            overrides.InputProps = {
                startAdornment: (
                    <Tooltip
                        title='Direction indicates the transaction type: ‘C’ stands for Credit (funds received) and ‘D’ stands for Debit (funds withdrawn).'
                        arrow
                    >
                        <InfoIcon
                            color='info'
                        />
                    </Tooltip>
                )
            };
        }

        const narrowed = narrowedOptionsByFilterId.get(filter.props.id);
        if (narrowed) {
            const { options: fullOptions = [] } = getFilterOptions(filter.props.id) as { options?: Option[] };
            const selectedIds = collectSelectedIds((filter.props as { value?: unknown }).value);
            const narrowedIds = new Set(narrowed.map(option => String(option.id)));
            const preservedSelected = fullOptions.filter(option =>
                selectedIds.has(String(option.id)) && !narrowedIds.has(String(option.id))
            );
            overrides.options = [...narrowed, ...preservedSelected];
        }

        return overrides as Filter<AbstractFilterProps>;
    };
};
